import { useState } from 'react';
import { ChevronDown, Eye, EyeOff, Ghost, Layers } from 'lucide-react';
import { crownArch, jawColour } from '@/lib/jaw-colours';
import { cn } from '@/lib/utils';
import type { LayerKind, LayerSpec } from '@/types';
import { FloatingPanel } from './FloatingPanel';

/**
 * What is showing, and how solid it is - floating top-right, where the
 * workbench keeps its mesh list.
 *
 * Opacity rather than on/off, because the useful states are in between: the
 * antagonist at a quarter so the crown's occlusal surface shows through it, the
 * prepared jaw at half to see the crown seated in it. "Ghost scans" is the
 * workbench's set-all-transparent, for looking at the crowns alone in context.
 */
const SWATCH: Record<LayerKind, string> = {
  crown: 'bg-[#f3ead9] ring-[#d9c7a6]',
  // Jaws: the colour comes from lib/jaw-colours (the crown's jaw and the
  // opposing jaw differ), set inline below; this is only the ring.
  lower: 'ring-[#a98a96]',
  upper: 'ring-[#a98a96]',
  bite: 'bg-[#b9c4d6] ring-[#94a3b8]',
  previous: 'bg-[#60a5fa] ring-[#3b82f6]',
  preop: 'ring-[#a98a96]',
};

interface LayersPanelProps {
  layers: LayerSpec[];
  opacity: Record<string, number>;
  onOpacity: (id: string, value: number) => void;
  showMargins: boolean;
  onShowMargins: (show: boolean) => void;
  defaultOpen?: boolean;
}

export function LayersPanel({
  layers,
  opacity,
  onOpacity,
  showMargins,
  onShowMargins,
  defaultOpen = true,
}: LayersPanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  // What each layer was before it was hidden, so showing it again restores
  // the dentist's own setting rather than snapping to fully solid.
  const [remembered, setRemembered] = useState<Record<string, number>>({});
  const arch = crownArch(
    layers.filter((layer) => layer.kind === 'crown').map((layer) => layer.tooth),
  );
  const swatchStyle = (layer: LayerSpec) => {
    const colour = layer.colour ?? jawColour(layer.kind, arch);
    return colour ? { backgroundColor: colour } : undefined;
  };

  const scans = layers.filter((layer) => layer.kind !== 'crown');
  const ghosted = scans.every((layer) => (opacity[layer.id] ?? 0) <= 0.3);

  const toggle = (layer: LayerSpec) => {
    const current = opacity[layer.id] ?? 0;
    if (current > 0.01) {
      setRemembered((was) => ({ ...was, [layer.id]: current }));
      onOpacity(layer.id, 0);
    } else {
      onOpacity(layer.id, remembered[layer.id] ?? 1);
    }
  };

  return (
    <FloatingPanel className="w-64 overflow-hidden">
      <button
        type="button"
        onClick={() => {
          setOpen((was) => !was);
        }}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
      >
        <Layers className="size-4 text-neutral-500" />
        <span className="text-[13px] font-semibold text-neutral-800 dark:text-neutral-100">
          Layers
        </span>
        <ChevronDown
          className={cn(
            'ml-auto size-4 text-neutral-400 transition-transform',
            !open && '-rotate-90',
          )}
        />
      </button>

      {open && (
        <div className="border-t border-neutral-900/5 px-3 pt-2 pb-3 dark:border-white/10">
          <ul className="space-y-2.5">
            {layers.map((layer) => {
              const value = opacity[layer.id] ?? 0;
              const visible = value > 0.01;
              return (
                <li key={layer.id} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn('size-3 shrink-0 rounded-full ring-1', SWATCH[layer.kind])}
                      // A crown's swatch is its shade, a jaw's its arch colour, as drawn.
                      style={swatchStyle(layer)}
                    />
                    <span
                      className={cn(
                        'flex-1 truncate text-[12.5px] font-medium',
                        visible ? 'text-neutral-800 dark:text-neutral-100' : 'text-neutral-400',
                      )}
                    >
                      {layer.label}
                    </span>
                    <span className="w-9 text-right font-mono text-[11px] text-neutral-500 tabular-nums">
                      {Math.round(value * 100)}%
                    </span>
                    <button
                      type="button"
                      aria-label={visible ? `Hide ${layer.label}` : `Show ${layer.label}`}
                      onClick={() => {
                        toggle(layer);
                      }}
                      className="rounded p-0.5 text-neutral-500 hover:bg-neutral-900/5 hover:text-neutral-900 dark:hover:bg-white/10 dark:hover:text-white"
                    >
                      {visible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                    </button>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={Math.round(value * 100)}
                    aria-label={`${layer.label} opacity`}
                    onChange={(event) => {
                      onOpacity(layer.id, Number(event.target.value) / 100);
                    }}
                    className="stage-range w-full"
                  />
                </li>
              );
            })}
          </ul>

          <div className="mt-3 flex items-center gap-2 border-t border-neutral-900/5 pt-2.5 dark:border-white/10">
            <label className="flex flex-1 cursor-pointer items-center gap-2 text-[12.5px] font-medium text-neutral-700 dark:text-neutral-200">
              <input
                type="checkbox"
                checked={showMargins}
                onChange={(event) => {
                  onShowMargins(event.target.checked);
                }}
                className="size-3.5 accent-[#0ea5a4]"
              />
              <span className="h-0.5 w-4 rounded-full bg-[#0ea5a4]" />
              Margin line
            </label>
            <button
              type="button"
              onClick={() => {
                for (const layer of scans) onOpacity(layer.id, ghosted ? layer.opacity : 0.25);
              }}
              className={cn(
                'flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] font-medium transition-colors',
                ghosted
                  ? 'bg-primary/10 text-primary'
                  : 'text-neutral-600 hover:bg-neutral-900/5 dark:text-neutral-300 dark:hover:bg-white/10',
              )}
            >
              <Ghost className="size-3.5" />
              {ghosted ? 'Restore' : 'Ghost scans'}
            </button>
          </div>
        </div>
      )}
    </FloatingPanel>
  );
}
